// Recovery on the run page (checkpoints contract §5-G, §7): a paused, failed
// or blocked run with a checkpoint can be resumed now, at a set time, or
// handed to another agent with the same role. A handoff makes a new run;
// RunChainNav links the runs of that chain (oldest first).

import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  RunStatus,
  type Run,
  type RunChainEntry,
  type RunRecoveryState,
} from '@goblic/shared'
import { ApiError, runsApi } from '../lib/api-client'
import {
  FAILURE_KIND_LABEL,
  HANDOFF_REASON_LABEL,
  candidateUnavailableReason,
  formatUntil,
  fromDatetimeLocal,
  recoveryErrorMessage,
  recoveryView,
  toDatetimeLocal,
} from '../lib/run-recovery'
import { formatRelative, humanize, shortId } from '../lib/format'
import { Notice, Panel } from './ui'
import { StatusBadge } from './status-badge'

const RECOVERY_STATUSES: readonly string[] = [
  RunStatus.Paused,
  RunStatus.Failed,
  RunStatus.Blocked,
  RunStatus.HandedOff,
]

/** The run's recovery state; only fetched where it can matter. */
export function useRunRecovery(run: Run | undefined) {
  const relevant =
    run != null &&
    (RECOVERY_STATUSES.includes(run.status) ||
      run.parentRunId != null ||
      run.childRunId != null)
  return useQuery({
    queryKey: ['run', run?.id, 'recovery', run?.status],
    queryFn: () => runsApi.recovery(run!.id),
    enabled: relevant,
    // An API error (e.g. 501 from an older API) means no panel; don't retry it.
    retry: (count, error) => !(error instanceof ApiError) && count < 2,
    refetchInterval: run?.status === RunStatus.Paused ? 30_000 : false,
  })
}

// ---------------------------------------------------------------------------
// Chain navigation
// ---------------------------------------------------------------------------

/**
 * "Handoff chain: Claude (Handed off) → GPT-5 (Running)" with links, or —
 * without the recovery state — plain links to the parent/child run.
 */
export function RunChainNav({
  run,
  chain,
}: {
  run: Run
  chain: RunChainEntry[] | undefined
}) {
  if (chain && chain.length > 1) {
    return (
      <nav aria-label="Handoff chain" className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm">
        <span className="text-ink-3">Handoff chain:</span>
        {chain.map((entry, index) => {
          const isCurrent = entry.runId === run.id
          const label = (
            <>
              <span className={isCurrent ? 'font-semibold text-ink' : ''}>
                {entry.agentName ?? `Run ${shortId(entry.runId)}`}
              </span>
              {entry.model ? <span className="text-ink-3"> ({entry.model})</span> : null}{' '}
              <StatusBadge status={entry.status} />
            </>
          )
          return (
            <span key={entry.runId} className="flex items-center gap-2">
              {index > 0 ? (
                <span aria-hidden className="text-ink-3" title={chain[index - 1]?.handoffReason ? HANDOFF_REASON_LABEL[chain[index - 1]!.handoffReason!] : undefined}>
                  →
                </span>
              ) : null}
              {isCurrent ? (
                <span aria-current="page" className="flex items-center gap-1">
                  {label}
                </span>
              ) : (
                <Link
                  to="/runs/$runId"
                  params={{ runId: entry.runId }}
                  className="flex items-center gap-1 hover:underline"
                >
                  {label}
                </Link>
              )}
            </span>
          )
        })}
      </nav>
    )
  }
  if (!run.parentRunId && !run.childRunId) return null
  return (
    <nav aria-label="Handoff chain" className="mb-6 flex flex-wrap gap-x-4 text-sm text-ink-3">
      {run.parentRunId ? (
        <span>
          Took over from{' '}
          <Link to="/runs/$runId" params={{ runId: run.parentRunId }} className="link font-mono">
            run {shortId(run.parentRunId)}
          </Link>
        </span>
      ) : null}
      {run.childRunId ? (
        <span>
          Handed off to{' '}
          <Link to="/runs/$runId" params={{ runId: run.childRunId }} className="link font-mono">
            run {shortId(run.childRunId)}
          </Link>
        </span>
      ) : null}
    </nav>
  )
}

// ---------------------------------------------------------------------------
// Recovery panel
// ---------------------------------------------------------------------------

function defaultResumeAt(state: RunRecoveryState, now: Date): Date {
  const resetsAt = state.failure?.resetsAt ? new Date(state.failure.resetsAt) : null
  if (resetsAt && resetsAt.getTime() > now.getTime()) return resetsAt
  return new Date(now.getTime() + 60 * 60_000)
}

/** The error of a failed resume/hand-off request (409: workflow moved on, 422: agent can't take over). */
export function RecoveryErrorNotice({ error }: { error: unknown }) {
  return <Notice tone="error">{recoveryErrorMessage(error)}</Notice>
}

/**
 * "Handed off. Open the new run": shown by the run page, not the panel — the
 * handoff turns this run `handed_off`, which unmounts the panel.
 */
export function HandedOffNotice({ childRunId }: { childRunId: string }) {
  return (
    <Notice tone="success">
      Handed off.{' '}
      <Link to="/runs/$runId" params={{ runId: childRunId }} className="link">
        Open the new run
      </Link>
    </Notice>
  )
}

export function RunRecoveryPanel({
  run,
  state,
  initialMode = 'none',
  onHandedOff,
}: {
  run: Run
  state: RunRecoveryState
  /** Called with the new run's id once a hand-off was accepted. */
  onHandedOff?: (childRunId: string) => void
  /** Which inline form starts open (the "Resume at…" / "Hand off…" button toggles it). */
  initialMode?: 'none' | 'at' | 'handoff'
}) {
  const queryClient = useQueryClient()
  const view = recoveryView(state)
  const [mode, setMode] = useState<'none' | 'at' | 'handoff'>(initialMode)
  const [at, setAt] = useState(() => toDatetimeLocal(defaultResumeAt(state, new Date())))
  const [agentId, setAgentId] = useState('')

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['run', run.id] })
    void queryClient.invalidateQueries({ queryKey: ['runs'] })
    void queryClient.invalidateQueries({ queryKey: ['task', run.taskId] })
  }

  const resume = useMutation({
    mutationFn: (body: { at?: string }) => runsApi.resume(run.id, body),
    onSuccess: () => {
      setMode('none')
      refresh()
    },
  })
  const handoff = useMutation({
    mutationFn: (id: string) => runsApi.handoff(run.id, { agentId: id }),
    onSuccess: (response) => {
      setMode('none')
      if (response.childRunId) onHandedOff?.(response.childRunId)
      refresh()
    },
  })
  const busy = resume.isPending || handoff.isPending
  // A stale error (say a 422 from "Resume now") doesn't belong to the form that's opened next.
  function clearErrors(): void {
    resume.reset()
    handoff.reset()
  }
  const error = resume.error ?? handoff.error

  if (!view.visible) return null

  const checkpoint = state.checkpoint
  const atIso = fromDatetimeLocal(at)
  const atInPast = atIso != null && new Date(atIso).getTime() <= Date.now()
  const selected = view.candidates.find((c) => c.agentId === agentId)

  return (
    <Panel
      title="Resume or hand off"
      description={
        checkpoint
          ? `Picks up from the checkpoint of ${formatRelative(checkpoint.createdAt)}, with the agent’s changes${checkpoint.hasSession ? ' and conversation' : ''}.`
          : 'This run has no checkpoint to pick up from.'
      }
      className="mb-6"
    >
      <div className="space-y-4">
        {state.resumeAfter ? (
          <Notice tone="signal">
            Resumes by itself at {formatUntil(state.resumeAfter)}.
          </Notice>
        ) : null}
        {state.failure ? (
          <p className="text-base text-ink-2">
            <span className="font-medium text-ink">
              {FAILURE_KIND_LABEL[state.failure.kind]}:
            </span>{' '}
            {state.failure.message}
            {state.failure.resetsAt ? (
              <span className="text-ink-3"> · resets {formatUntil(state.failure.resetsAt)}</span>
            ) : null}
          </p>
        ) : null}
        {checkpoint?.worklog && (checkpoint.worklog.done.length || checkpoint.worklog.next.length) ? (
          <div className="grid gap-3 text-sm sm:grid-cols-2">
            {checkpoint.worklog.done.length ? (
              <div>
                <p className="font-medium text-ink-2">Done so far</p>
                <ul className="mt-1 list-disc pl-5 text-ink-3">
                  {checkpoint.worklog.done.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {checkpoint.worklog.next.length ? (
              <div>
                <p className="font-medium text-ink-2">Next</p>
                <ul className="mt-1 list-disc pl-5 text-ink-3">
                  {checkpoint.worklog.next.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={!view.resumeNow.enabled || busy}
            title={view.resumeNow.reason}
            onClick={() => resume.mutate({})}
          >
            {resume.isPending && mode === 'none' ? 'Resuming…' : 'Resume now'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={!view.resumeAt.enabled || busy}
            title={view.resumeAt.reason}
            aria-expanded={mode === 'at'}
            onClick={() => {
              clearErrors()
              setMode(mode === 'at' ? 'none' : 'at')
            }}
          >
            Resume at…
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={!view.handoff.enabled || busy}
            title={view.handoff.reason}
            aria-expanded={mode === 'handoff'}
            onClick={() => {
              clearErrors()
              setMode(mode === 'handoff' ? 'none' : 'handoff')
            }}
          >
            Hand off to agent…
          </button>
        </div>
        {!view.resumeNow.enabled && view.resumeNow.reason && !view.handoff.enabled ? (
          <p className="text-sm text-ink-3">{view.resumeNow.reason}</p>
        ) : null}

        {mode === 'at' ? (
          <form
            className="flex flex-wrap items-end gap-2 rounded-control border border-line-strong p-3"
            onSubmit={(event) => {
              event.preventDefault()
              if (atIso && !atInPast) resume.mutate({ at: atIso })
            }}
          >
            <label className="text-sm text-ink-2" htmlFor={`resume-at-${run.id}`}>
              <span className="mb-1 block font-medium text-ink">Resume at</span>
              <input
                id={`resume-at-${run.id}`}
                type="datetime-local"
                className="field-input"
                value={at}
                min={toDatetimeLocal(new Date())}
                onChange={(event) => setAt(event.target.value)}
                required
              />
            </label>
            <button
              type="submit"
              className="btn-primary"
              disabled={busy || !atIso || atInPast}
            >
              {resume.isPending ? 'Scheduling…' : 'Schedule'}
            </button>
            {atInPast ? (
              <p className="w-full text-sm text-danger">Pick a time in the future.</p>
            ) : null}
          </form>
        ) : null}

        {mode === 'handoff' ? (
          <form
            className="space-y-2 rounded-control border border-line-strong p-3"
            onSubmit={(event) => {
              event.preventDefault()
              if (selected?.available) handoff.mutate(selected.agentId)
            }}
          >
            <label className="block text-sm" htmlFor={`handoff-agent-${run.id}`}>
              <span className="mb-1 block font-medium text-ink">Hand off to</span>
              <select
                id={`handoff-agent-${run.id}`}
                className="field-input"
                value={agentId}
                onChange={(event) => setAgentId(event.target.value)}
                required
              >
                <option value="" disabled>
                  Choose an agent
                </option>
                {view.candidates.map((candidate) => {
                  const unavailable = candidateUnavailableReason(candidate)
                  return (
                    <option
                      key={candidate.agentId}
                      value={candidate.agentId}
                      disabled={!candidate.available}
                    >
                      {candidate.name}
                      {candidate.model ? ` (${candidate.model})` : ''}
                      {candidate.fallback ? ' · fallback' : ''}
                      {unavailable ? ` · ${unavailable}` : ''}
                    </option>
                  )
                })}
              </select>
            </label>
            <p className="text-sm text-ink-3">
              A new run on that agent starts from this run’s changes and its work log. This run
              stays as it is, marked {humanize(RunStatus.HandedOff).toLowerCase()}.
            </p>
            <div className="flex justify-end">
              <button
                type="submit"
                className="btn-primary"
                disabled={busy || !selected?.available}
              >
                {handoff.isPending ? 'Handing off…' : 'Hand off'}
              </button>
            </div>
          </form>
        ) : null}

        {error ? <RecoveryErrorNotice error={error} /> : null}
        {resume.isSuccess && resume.data.resumeAfter ? (
          <Notice tone="success">Scheduled for {formatUntil(resume.data.resumeAfter)}.</Notice>
        ) : null}
      </div>
    </Panel>
  )
}
